import { useMemo } from 'react'

import { FlattenedDocumentPage } from '@/lib/api/client'
import { useListTemplateAttributes } from '@/services/hooks/template_attributes'
import { DraggableAnnotation } from './Annotations/DraggableAnnotation'
import { OperationNotesEditor } from './OperationNotesEditor/OperationNotesEditor'
import { OperationLabel } from './OperationLabel'
import DraggableImage from './DraggableImage'
import { DraggableVideo } from './DraggableVideo'
import { PartsTable } from './PartsTable'
import { ToolsTable } from './ToolsTable'
import GenericTable from './GenericTable'

const DraggableElementsMap = {
  annotation: DraggableAnnotation,
  note: OperationNotesEditor,
  label: OperationLabel,
  image: DraggableImage,
  view: DraggableImage,
  video: DraggableVideo,
  'parts-table': PartsTable,
  'tools-table': ToolsTable,
  'generic-table': GenericTable,
}

const MemoComponent = ({
  Component,
  template,
  isReadOnly,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}) => {
  return useMemo(
    () => (
      <Component
        template={template}
        isReadOnly={isReadOnly}
        documentPage={documentPage}
        dataType={dataType}
        onNudgeStart={onNudgeStart}
        onNudgeEnd={onNudgeEnd}
      />
    ),
    [template.id, template.template_values, isReadOnly, documentPage.id],
  )
}

type DraggableElementsProps = {
  isReadOnly: boolean
  documentPage: FlattenedDocumentPage
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}
export const DraggableElements = ({
  isReadOnly,
  documentPage,
  onNudgeStart,
  onNudgeEnd,
}: DraggableElementsProps) => {
  const { data: templateAttributes } = useListTemplateAttributes({
    documentPageId: documentPage.id,
  })

  return templateAttributes?.map((templateAttribute) => {
    const dataType =
      templateAttribute.data_type !== 'table'
        ? templateAttribute.data_type
        : templateAttribute.template_values?.type

    const Component = DraggableElementsMap[dataType] || (() => <></>)

    return (
      <MemoComponent
        Component={Component}
        key={templateAttribute.id}
        template={templateAttribute}
        isReadOnly={isReadOnly}
        documentPage={documentPage}
        dataType={dataType}
        onNudgeStart={onNudgeStart}
        onNudgeEnd={onNudgeEnd}
      />
    )
  })
}
